/**
 * Shared per-row chips for Jira issue type + priority.
 *
 * Used by:
 *   - The project Jira tab (JiraCard)
 *   - The Synnergie Workload page (SynnergieWorkload)
 *
 * Designed icon-only by default to fit the dense column layout, with
 * hover tooltips that carry the raw Jira value. Both components no-op
 * gracefully when the underlying value is null/empty so a row missing
 * a type or priority just renders an em-dash.
 */
import {
  Bug,
  BookOpen,
  CheckSquare,
  ChevronsDown,
  ChevronsUp,
  ChevronDown,
  ChevronUp,
  Equal,
  Layers,
  Square,
  Sparkles,
  AlertTriangle,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Issue type
// ---------------------------------------------------------------------------

interface TypeMeta {
  icon: LucideIcon;
  /** Tailwind text color for the icon. */
  color: string;
  /** Pill background — used when rendered with the label. */
  bg: string;
}

const TYPE_BY_KEY: Record<string, TypeMeta> = {
  epic:        { icon: Layers,       color: "text-violet-600 dark:text-violet-400",  bg: "bg-violet-50 dark:bg-violet-500/10" },
  story:       { icon: BookOpen,     color: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-50 dark:bg-emerald-500/10" },
  task:        { icon: CheckSquare,  color: "text-sky-600 dark:text-sky-400",     bg: "bg-sky-50 dark:bg-sky-500/10" },
  bug:         { icon: Bug,          color: "text-rose-600 dark:text-rose-400",    bg: "bg-rose-50 dark:bg-rose-500/10" },
  "sub-task":  { icon: Square,       color: "text-ink-muted",   bg: "bg-canvas" },
  subtask:     { icon: Square,       color: "text-ink-muted",   bg: "bg-canvas" },
  improvement: { icon: Sparkles,     color: "text-amber-600 dark:text-amber-400",   bg: "bg-amber-50 dark:bg-amber-500/10" },
  enhancement: { icon: Sparkles,     color: "text-amber-600 dark:text-amber-400",   bg: "bg-amber-50 dark:bg-amber-500/10" },
  incident:    { icon: AlertTriangle, color: "text-rose-600 dark:text-rose-400",   bg: "bg-rose-50 dark:bg-rose-500/10" },
};

const TYPE_DEFAULT: TypeMeta = {
  icon: Square, color: "text-ink-subtle", bg: "bg-canvas",
};

function typeMeta(type: string | null | undefined): TypeMeta {
  if (!type) return TYPE_DEFAULT;
  return TYPE_BY_KEY[type.toLowerCase().trim()] ?? TYPE_DEFAULT;
}

export function IssueTypeChip({
  type,
  size = "sm",
  showLabel = false,
}: {
  type: string | null | undefined;
  size?: "sm" | "xs";
  showLabel?: boolean;
}) {
  if (!type) return <span className="text-ink-faint">—</span>;
  const meta = typeMeta(type);
  const Icon = meta.icon;
  const iconSize = size === "xs" ? "h-3 w-3" : "h-3.5 w-3.5";
  if (showLabel) {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium",
          meta.bg, meta.color,
        )}
        title={type}
      >
        <Icon className={iconSize} />
        {type}
      </span>
    );
  }
  return (
    <span
      className={cn("inline-flex items-center justify-center", meta.color)}
      title={type}
      aria-label={`Issue type: ${type}`}
    >
      <Icon className={iconSize} />
    </span>
  );
}

// ---------------------------------------------------------------------------
// Priority
// ---------------------------------------------------------------------------

interface PriorityMeta {
  icon: LucideIcon;
  color: string;
  /** Sort weight — lets callers order by perceived urgency. */
  weight: number;
}

const PRIORITY_BY_KEY: Record<string, PriorityMeta> = {
  highest: { icon: ChevronsUp,   color: "text-rose-600 dark:text-rose-400",   weight: 5 },
  high:    { icon: ChevronUp,    color: "text-rose-500",   weight: 4 },
  medium:  { icon: Equal,        color: "text-amber-500",  weight: 3 },
  low:     { icon: ChevronDown,  color: "text-sky-500",    weight: 2 },
  lowest:  { icon: ChevronsDown, color: "text-ink-subtle",  weight: 1 },
};

export function PriorityChip({
  priority,
  size = "sm",
  showLabel = false,
}: {
  priority: string | null | undefined;
  size?: "sm" | "xs";
  showLabel?: boolean;
}) {
  if (!priority) return <span className="text-ink-faint">—</span>;
  const meta = PRIORITY_BY_KEY[priority.toLowerCase().trim()];
  if (!meta) {
    // Unknown priority value — render the raw text so we don't lose info,
    // even if we can't pick an icon.
    return (
      <span className="text-[10px] text-ink-muted" title={priority}>
        {priority}
      </span>
    );
  }
  const Icon = meta.icon;
  const iconSize = size === "xs" ? "h-3 w-3" : "h-3.5 w-3.5";
  if (showLabel) {
    return (
      <span
        className={cn("inline-flex items-center gap-0.5 text-[10px] font-medium", meta.color)}
        title={`Priority: ${priority}`}
      >
        <Icon className={iconSize} />
        {priority}
      </span>
    );
  }
  return (
    <span
      className={cn("inline-flex items-center justify-center", meta.color)}
      title={`Priority: ${priority}`}
      aria-label={`Priority: ${priority}`}
    >
      <Icon className={iconSize} />
    </span>
  );
}

export function priorityWeight(priority: string | null | undefined): number {
  if (!priority) return 0;
  return PRIORITY_BY_KEY[priority.toLowerCase().trim()]?.weight ?? 0;
}
